import { Controller } from "stimulus";
import hideElement from "../../functions/hideElement";
import showElement from "../../functions/showElement";
import disableElement from "../../functions/disableElement";
import enableElement from "../../functions/enableElement";

// The "Display Signature Images" switch on the create share link form. The row carries a map
// of every list on the page, {id: {display_signature, blocked}};
// each time the list selection changes the switch is re-derived from the selected lists: all On
// -> On, all Off -> Off, mixed -> Off with the hint, any blocked -> disabled at Off with the
// notice. Once the user has touched the switch their On/Off stands, but a blocked list still
// disables it at Off, since the server refuses On for that list; their choice returns when the
// blocked list is removed.
export default class extends Controller {
  static targets = ["switch", "mixedHint", "blockedHint"];

  static values = { lists: Object };

  // A Stimulus value getter parses the attribute on every read; the map is read once per selected
  // id on every selection change, and Select All changes the selection once per list, so it is
  // parsed here once and kept.
  connect() {
    this.touched = false;
    this.lists = this.listsValue;
  }

  // The checkbox has already toggled when its click reaches here, so this is the user's choice.
  touch() {
    this.touched = true;
    this.choice = this.switchTarget.checked;
  }

  // Materialize renders the list picker as a dropdown of checkboxes over the hidden select;
  // the select's own change event and clicks inside the picker both mean the selection may have
  // moved, and the select is read after the picker's own handlers have run.
  listsChanged(event) {
    const select = this.listSelect;

    if (!select || !(event.target === select || event.target.closest("#base_docs-wrapper"))) {
      return;
    }

    this.apply(this.derive(this.selectedIds));
  }

  derive(ids) {
    const entries = ids.map((id) => this.lists[id]).filter(Boolean);
    const blocked = entries.some((entry) => entry.blocked);
    const values = [...new Set(entries.map((entry) => entry.display_signature))];

    return { blocked, mixed: values.length > 1, on: values.length === 1 && values[0] === true };
  }

  apply({ blocked, mixed, on }) {
    if (blocked) {
      this.switchTarget.checked = false;
      disableElement(this.switchTarget);
    } else {
      enableElement(this.switchTarget);
      // A blocked list forces Off; once it is removed the user's own choice comes back.
      this.switchTarget.checked = this.touched ? this.choice : on;
    }

    this.show(this.mixedHintTarget, mixed && !blocked);
    this.show(this.blockedHintTarget, blocked);
  }

  show(element, visible) {
    if (visible) {
      showElement(element, "block");
    } else {
      hideElement(element);
    }
  }

  get listSelect() {
    return document.querySelector("select[name='base_doc_ids[]']");
  }

  get selectedIds() {
    return Array.from(this.listSelect.selectedOptions)
      .map((option) => option.value)
      .filter((value) => value !== "");
  }
}
